import { format, startOfMonth, UTCDate } from '@/utils/dates';
import { CategoryView, TransactionView } from '../../../types/entity-types';
import { isTransactionInternalTransfer } from './filter-internal-transfers';

export type CashFlowChartDataPoint = {
  date: Date;
  period: string; // Label like "Nov 2024"
  moneyIn: number;
  moneyOut: number;
};

type GetCashFlowChartDataArgs = {
  transactions: TransactionView[];
  categories: CategoryView[];
  startDate: Date;
  endDate: Date;
};

export const getCashFlowChartData = ({
  transactions,
  categories,
  startDate,
  endDate,
}: GetCashFlowChartDataArgs): CashFlowChartDataPoint[] => {
  // 1. Build maps for category lookups
  const categoryLevel1Map = new Map<string, string>();
  categories.forEach((cat) => {
    if (cat.id) {
      categoryLevel1Map.set(cat.id, cat.level1 || '');
    }
  });

  // Helper to resolve transaction to Level 1 Category Name
  // Uses the embedded vendor object from the transaction (more reliable than lookup)
  const getTransactionLevel1 = (t: TransactionView): string => {
    const vendor = t.vendor;

    if (vendor?.category) {
      // If vendor has category object with level1, use it directly
      return vendor.category.level1 || '';
    }

    if (vendor?.categoryId) {
      // Fallback to map lookup if category object not available
      return categoryLevel1Map.get(vendor.categoryId) || '';
    }

    return '';
  };

  // 2. Initialize monthly buckets
  const monthlyData = new Map<
    string,
    {
      date: Date;
      revenue: number;
      operatingExpenses: number;
      otherExpenses: number;
    }
  >();

  let loopDate = startOfMonth(startDate);
  while (loopDate <= endDate) {
    const key = format(loopDate, 'yyyy-MM');
    monthlyData.set(key, {
      date: loopDate,
      revenue: 0,
      operatingExpenses: 0,
      otherExpenses: 0,
    });
    loopDate = startOfMonth(new UTCDate(loopDate.getFullYear(), loopDate.getMonth() + 1, 1));
  }

  // 3. Iterate transactions and aggregate
  transactions.forEach((t) => {
    if (!t.fspDate) return;
    // Exclude internal transfers (Financial Items > Internal Transfers)
    if (isTransactionInternalTransfer(t)) return;
    const tDate = new UTCDate(t.fspDate);
    if (tDate < startDate || tDate > endDate) return;

    const monthKey = format(tDate, 'yyyy-MM');
    const bucket = monthlyData.get(monthKey);
    if (!bucket) return;

    const amount = t.baseCurrencyAmount || 0;
    // Amount is positive for Credit, Negative for Debit in the view?
    // Usually TransactionView.baseCurrencyAmount is signed?
    // Let's verify: In calculate-monthly-breakdown:
    // const amount = t.isCredit ? new Big(t.baseCurrencyAmount) : new Big(t.baseCurrencyAmount).times(-1);

    const signedAmount = t.isCredit ? amount : -amount;

    const level1Name = getTransactionLevel1(t);

    // Handle Financial Items specially per the formula:
    // - Financial Items credits (e.g., interest income) → add to revenue (Money In)
    // - Financial Items debits (e.g., bank fees) → add to expenses (Money Out)
    // Note: Internal Transfers are already excluded above (line 78)
    if (level1Name === 'Financial Items') {
      if (t.isCredit) {
        bucket.revenue += signedAmount;
      } else {
        bucket.otherExpenses += signedAmount;
      }
      return;
    }

    if (level1Name === 'Revenue') {
      bucket.revenue += signedAmount;
    } else if (level1Name === 'Operating Expenses') {
      bucket.operatingExpenses += signedAmount;
    } else {
      bucket.otherExpenses += signedAmount;
    }
  });

  // 4. Convert to Chart Points with Formula
  const results: CashFlowChartDataPoint[] = [];
  monthlyData.forEach((bucket) => {
    // Money In = SUM(Revenue) + Financial Items credits (e.g., interest income)
    // Money Out = SUM(All Expenses) + Financial Items debits (e.g., bank fees)
    // Note: Only Internal Transfers are excluded, other Financial Items are included

    // All Expenses = Operating Expenses + Other Expenses (includes Financial Items debits)
    const allExpenses = bucket.operatingExpenses + bucket.otherExpenses;

    const moneyIn = bucket.revenue;
    const moneyOut = Math.abs(allExpenses);

    results.push({
      date: bucket.date,
      period: format(bucket.date, 'MMM'), // e.g. Nov (without year)
      moneyIn,
      moneyOut,
    });
  });

  // Sort by date
  return results.sort((a, b) => a.date.getTime() - b.date.getTime());
};
